import { IconCheck, IconClipboardCopy } from "@tabler/icons-react";
import configActions from "actions/configs";
import layoutActions from "actions/layout";
import modalActions from "actions/modal";
import profileActions from "actions/profile";
import TechSupportWidgets from "components/techSupportWidgets";
import { organizationRegistrationStatusesEnum } from "helpers/enums";
import { paths } from "helpers/paths";
import { connect, generatedActions } from "helpers/redux";
import { isDesktop } from "helpers/system";
import Person from "person.svg";
import React, { useEffect, useState } from "react";
import { Route, Switch } from "react-router-dom";
import { del as logoff } from "sources/session";
import AddCars from "./addCars";
import AtiSuImport from "./atiSuImport";
import CreateRelay from "./createRelay";
import RegistrationContinuationEmail from "./registrationContinuationEmail";

const copyTextToClipboard = (text) => {
    const copyWithTextarea = () => {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();

        const copied = document.execCommand("copy");
        document.body.removeChild(textarea);

        return copied;
    };

    if (!navigator.clipboard?.writeText) {
        return Promise.resolve(copyWithTextarea());
    }

    return navigator.clipboard.writeText(text).then(
        () => true,
        () => copyWithTextarea(),
    );
};

const RegistrationDesktopModal = ({ email, canSendEmail, onClose }) => {
    const [copied, setCopied] = useState(false);

    const setCopiedTemporarily = () => {
        setCopied(true);
        setTimeout(() => {
            setCopied(false);
        }, 2000);
    };

    const copyToClipboard = () => {
        if (copied) {
            return null;
        }

        copyTextToClipboard(window.location.href).then((copySucceeded) => {
            if (copySucceeded) {
                setCopiedTemporarily();
            }
        });
    };

    return (
        <>
            <div className="r-relay-help__backdrop" onClick={onClose} />
            <div
                className="r-relay-help r-relay-help--center"
                role="dialog"
                aria-modal="true"
                aria-labelledby="relay-help-title"
            >
                <button className="r-relay-help__close" type="button" aria-label="Закрыть" onClick={onClose}>
                    ×
                </button>
                <div className="r-relay-help__title" id="relay-help-title">
                    Отлично, ваш личный
                    <br />
                    кабинет готов.
                </div>
                <div className="r-relay-help__text">
                    Рекомендуем открыть платформу <br />
                    на компьютере для удобной работы.
                </div>
                {email && canSendEmail && <RegistrationContinuationEmail email={email} />}
                <button className="r-relay-help__support-button" type="button" onClick={copyToClipboard}>
                    {copied ? (
                        <>
                            <IconCheck />
                            <span>Скопировано</span>
                        </>
                    ) : (
                        <>
                            <IconClipboardCopy />
                            <span>Скопировать ссылку на страницу</span>
                        </>
                    )}
                </button>
                <button className="r-relay-help__skip-button" type="button" onClick={onClose}>
                    Продолжить на телефоне
                </button>
            </div>
        </>
    );
};

const ConnectCars = (props) => {
    const [techSupportChatOpened, setTechSupportChatOpened] = useState(false);
    const [desktopModalOpen, setDesktopModalOpen] = useState(false);
    const {
        organization,
        roles,
        profile,
        location,
        jivoChatScriptUrl,
        getInitial,
        organizationLoaded,
        history,
        getDictionaries,
        loadConfigs,
    } = props;

    useEffect(() => {
        document.body.classList.add("registration-body-scroll");

        if (!isDesktop()) {
            setDesktopModalOpen(true);
        }
        getInitial().then(() => {
            getDictionaries();
            loadConfigs();
        });

        return () => document.body.classList.remove("registration-body-scroll");
    }, []);

    if (!organizationLoaded) {
        return null;
    }

    if (
        !organization.registrationStatus ||
        organization.registrationStatus === organizationRegistrationStatusesEnum.Completed
    ) {
        return history.push(paths.root);
    }

    const handleLogout = (e) => {
        e.preventDefault();
        logoff();
    };

    return (
        <div className="registration-page no-overflow h-100">
            <div className="registration-container__shadow-top" />
            <div className="registration-container__shadow" />
            <div className="registration-container__background" />
            <div className="identity-container">
                <div className="registration-container__header">
                    <div className="registration-container__header--logo" />
                    <div className="d-flex">
                        {process.env.APP_ENV !== "demo" && !!organization.id && !!profile.email && (
                            <TechSupportWidgets
                                location={location}
                                profile={profile}
                                organization={organization}
                                jivoChatScriptUrl={jivoChatScriptUrl}
                                roles={roles}
                                techSupportChatOpened={techSupportChatOpened}
                                setTechSupportChatOpened={setTechSupportChatOpened}
                            />
                        )}
                        <div className="registration-container__header--sign-in pl-3">
                            <Person /> {props.profile.email}
                        </div>
                        <a
                            className="header__link header__link--exit"
                            data-test-id="header-logout"
                            onClick={handleLogout}
                            title="Выход"
                            href="#"
                        />
                    </div>
                </div>
                <Switch location={location}>
                    <Route
                        exact
                        path={paths.registrationAddCars}
                        render={() => <AddCars organization={organization} getOrg={props.getOrg} enums={props.enums} />}
                    />
                    <Route
                        exact
                        path={paths.registrationCreateRelay}
                        render={() => (
                            <CreateRelay
                                getOrg={props.getOrg}
                                organization={organization}
                                onTechSupportOpen={() => setTechSupportChatOpened(true)}
                            />
                        )}
                    />
                    <Route
                        exact
                        path={paths.registrationAddDrivers}
                        render={() => <AtiSuImport getOrg={props.getOrg} type="drivers" />}
                    />
                    <Route
                        exact
                        path={paths.registrationAddTrucks}
                        render={() => <AtiSuImport getOrg={props.getOrg} type="trucks" />}
                    />
                </Switch>
                {desktopModalOpen && (
                    <RegistrationDesktopModal
                        email={profile.email}
                        canSendEmail={profile.id === organization.adminId}
                        onClose={() => setDesktopModalOpen(false)}
                    />
                )}
            </div>
        </div>
    );
};

const mapState = (state) => ({
    jivoChatScriptUrl: state.configs.jivoChatScriptUrl,
    organization: state.profile.organization,
    profile: state.profile.profile,
    roles: state.profile.roles,
    organizationLoaded: state.profile.organizationLoaded,
});

const mapDispatch = (dispatch) => ({
    toggleFullscreen: () => dispatch(layoutActions.toggleFullscreen()),
    toggleSidebar: (params) => dispatch(layoutActions.toggleSidebar(params)),
    modal: (params) => dispatch(modalActions.open(params)),
    getInitialgetInitial: () => dispatch(profileActions.getInitial()),
    getDictionaries: () => dispatch(generatedActions.dictionaries.index()),
    loadConfigs: () => dispatch(configActions.loadConfigs()),
});

export default connect(mapState, mapDispatch)(ConnectCars);
